'use client';

import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import Button from '@alltrails/denali/components/Button';
import Link from '@alltrails/denali/components/Link';
import Logo from '@alltrails/denali/components/Logo';
import SponsoredBadge from '@/components/shared/SponsoredBadge';
import { PostPreview } from '@/types/shared';
import styles from './RecentNews.module.scss';

type RecentNewsProps = {
  recentPosts: PostPreview[];
};

const RecentNews = ({ recentPosts }: RecentNewsProps) => {
  const languageRegionCode = useLanguageRegionCode();

  if (!recentPosts || recentPosts.length === 0) {
    return null;
  }

  const formatter = new Intl.DateTimeFormat(languageRegionCode, {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <Typography component="h2" variant="heading500" color="primary" className={styles.headerText}>
          <FormattedMessage defaultMessage="Recent news" />
        </Typography>
        <Button
          linkInfo={{ href: '/press#press-page-press-room' }}
          size="md"
          text={<FormattedMessage defaultMessage="See all" />}
          variant="default"
        />
      </div>
      <div className={styles.newsList}>
        {recentPosts.slice(0, 3).map(recentPost => (
          <Link href={`/press/${recentPost.slug}`} key={recentPost.id} className={styles.newsItem}>
            <div className={styles.imageWrapper}>
              {recentPost.hero_image?.full ? (
                <Image src={recentPost.hero_image.full} alt={recentPost.title} height={260} width={390} className={styles.image} />
              ) : (
                <div className={classNames(styles.image, styles.missingImagePlaceholder)}>
                  <Logo size="md" variant="logomark" color="colored-light" />
                </div>
              )}
            </div>
            {recentPost.category && <div className={styles.category}>{recentPost.category}</div>}
            <Typography component="h3" variant="heading300" color="primary" className={styles.title}>
              {recentPost.title}
            </Typography>
            {recentPost.is_sponsored && <SponsoredBadge className={styles.sponsoredMargin} />}
            <div className={styles.date}>{formatter.format(new Date(recentPost.date))}</div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default RecentNews;
